import {
  PreScreeningQuestion,
  QuestionType,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  DIABETES_ATTRIBUTES,
  CARDIOVASCULAR_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
  MENTAL_HEALTH_ATTRIBUTES,
  NEUROLOGICAL_ATTRIBUTES,
  HORMONE_ATTRIBUTES,
  CANCER_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";
import { createMedicationChecklist } from "./helpers/createMedicationChecklist";

/**
 * Pre-screening questions for Sparrow Diabetes Study
 *
 * Trial ID: VDIABCL2006
 *
 * Target population: Adults with Type 2 Diabetes and elevated cortisol
 *
 * 12 questions total. Q4 is an observational self-report medication count
 * (non-disqualifying, no attribute mapping) followed by Q5, a checkbox
 * medication recognition list that drives qualification and attribute mapping.
 * This dual-question approach measures discrepancy between self-reported
 * medication counts and recognition-based selection.
 *
 * Key inclusion criteria:
 * - Type 2 diabetes for at least 12 months
 * - A1c level of 7% or higher
 * - Taking at least 2 diabetes medications (determined by checkbox Q5)
 * - At least one qualifying condition (complications, Cushing's, etc.)
 *   OR taking 3+ medications (auto-qualifies)
 *
 * Key exclusion criteria:
 * - Hemolytic anemia, sickle cell disease
 * - Pheochromocytoma
 * - Eating disorder, MS
 * - Liver failure/cirrhosis
 * - Pregnant/breastfeeding
 * - Current cancer treatment
 * - ESKD/dialysis
 * - ITP
 * - Current clinical trial participation
 */
export const sparrowDiabetesScreeningQuestions: PreScreeningQuestion[] = [
  // Q1: Diabetes Type
  {
    id: "diabetes_type",
    type: QuestionType.RADIO,
    question: "What type of diabetes do you have?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "type2",
        label: "Type 2 diabetes",
      },
      {
        value: "type1",
        label: "Type 1 diabetes",
        disqualify: true,
      },
      {
        value: "no_diabetes",
        label: "I don't have diabetes",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study requires participants with type 2 diabetes.",
    attributeMapping: mappers.value({
      type2: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: true },
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE1, booleanAttributeValue: false },
      ],
      type1: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: false },
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE1, booleanAttributeValue: true },
      ],
      no_diabetes: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: false },
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE1, booleanAttributeValue: false },
      ],
    }),
  },

  // Q2: Diabetes Duration
  {
    id: "diabetes_duration",
    type: QuestionType.RADIO,
    question: "When were you diagnosed with type 2 diabetes?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "less_than_1_year",
        label: "Less than 1 year ago",
        disqualify: true,
      },
      {
        value: "more_than_1_year",
        label: "More than 1 year ago",
      },
    ],
    disqualifyMessage:
      "This study requires participants who have been diagnosed with type 2 diabetes for at least 12 months.",
    attributeMapping: mappers.value({
      more_than_1_year: [
        { attributeDefinitionId: 25713, booleanAttributeValue: true }, // has_type2diabetes_12_months
      ],
      less_than_1_year: [
        { attributeDefinitionId: 25713, booleanAttributeValue: false },
      ],
    }),
  },

  // Q3: A1c Level (threshold 7.5%, must be within last 2 months)
  {
    id: "a1c_level",
    type: QuestionType.RADIO,
    question: "What was your most recent HbA1c level, if measured in the past 2 months?",
    helpText:
      "HbA1c is a blood test that measures your average blood sugar level over the past 2-3 months.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "less_than_7_5",
        label: "HbA1c less than 7.5%",
        disqualify: true,
      },
      {
        value: "7_5_or_more",
        label: "HbA1c 7.5% or more",
      },
      {
        value: "test_over_2_months",
        label: "Test was more than 2 months ago",
        disqualify: true,
      },
      {
        value: "not_sure",
        label: "Not sure",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study requires participants with an A1c level of 7.5% or higher from a recent test.",
    attributeMapping: mappers.value({
      less_than_7_5: [
        { attributeDefinitionId: 26867, booleanAttributeValue: false }, // a1c_7.5_greater
      ],
      "7_5_or_more": [
        { attributeDefinitionId: 26867, booleanAttributeValue: true }, // a1c_7.5_greater
      ],
      test_over_2_months: [],
      not_sure: [],
    }),
  },

  // Q4: Medication Count Self-Report (observational only — no disqualification, no attribute mapping)
  {
    id: "diabetes_medications_self_report",
    type: QuestionType.RADIO,
    question: "How many medications do you take for your diabetes?",
    helpText:
      "Count each active ingredient in combination drugs as a separate medication. Examples include metformin, GLP-1 medications (Ozempic, Mounjaro), and SGLT2 inhibitors (Jardiance).",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no_medications",
        label: "No medications",
      },
      {
        value: "one_medication",
        label: "1 medication",
      },
      {
        value: "two_medications",
        label: "2 medications",
      },
      {
        value: "three_or_more",
        label: "3 or more medications",
      },
    ],
  },

  // Q5: Diabetes Medication Recognition Checklist (drives qualification and attribute mapping)
  createMedicationChecklist({
    id: "diabetes_medications_count",
    question: "Currently taking any of these medications?",
    helpText: "If you take a combination pill (like Janumet or Synjardy), it contains 2 medications — please select each ingredient below.",
    disqualifyMessage:
      "This study requires participants who are currently taking at least 2 diabetes medications.",
    minCount: 2,
    medications: [
      { value: "metformin", label: "Metformin (Glucophage)", sublabel: "Also in: Janumet, Synjardy, Xigduo XR, and other combination pills" },
      { value: "glp1", label: "Ozempic, Mounjaro, Trulicity, or Victoza", sublabel: "GLP-1 / GIP medications. Also includes Wegovy, Rybelsus, Byetta, Bydureon" },
      { value: "sglt2", label: "Jardiance, Farxiga, or Invokana", sublabel: "SGLT2 inhibitors. Also in: Synjardy, Xigduo XR, Glyxambi, and other combination pills" },
      { value: "dpp4", label: "Januvia, Tradjenta, or Onglyza", sublabel: "DPP-4 inhibitors. Also in: Janumet, Glyxambi, Jentadueto, and other combination pills" },
      { value: "insulin", label: "Insulin (Lantus, Humalog, Novolog, Tresiba, etc.)", sublabel: "All types including long-acting, rapid-acting, pens, and pumps" },
      { value: "sulfonylurea", label: "Glipizide, Glyburide, or Glimepiride", sublabel: "Sulfonylureas. Also in: Glucovance" },
      { value: "pioglitazone", label: "Pioglitazone (Actos)", sublabel: "TZD. Also in: ActoPlus Met, Oseni" },
      { value: "meglitinide", label: "Repaglinide or Nateglinide (Prandin, Starlix)", sublabel: "Meglitinides" },
    ],
    noneOption: { value: "none_diabetes_meds", label: "I don't take any diabetes medications" },
    otherOption: { value: "other_diabetes_med", label: "Another diabetes medication not listed above" },
    attributeThresholds: [
      { attributeDefinitionId: 26112, minCount: 2 }, // has_diabetes_is_taking_2_diabetic_medications
      { attributeDefinitionId: 23960, minCount: 3 }, // has_diabetic_medications_3_current
    ],
  }),

  // Q6: Diabetes Complications
  {
    id: "diabetes_complications",
    type: QuestionType.CHECKBOX,
    question: "Due to your diabetes, do any of the following apply to you?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "kidney_disease_diabetes",
        label: "I have kidney disease as a result of my diabetes",
      },
      {
        value: "foot_ulcers_diabetes",
        label: "I have foot ulcers as a result of my diabetes",
      },
      {
        value: "nerve_damage_diabetes",
        label: "I have nerve damage as a result of my diabetes",
      },
      {
        value: "eye_disease_diabetes",
        label: "I have eye disease as a result of my diabetes",
      },
      {
        value: "none_diabetes_complications",
        label: "None of these apply to me",
      },
    ],
    attributeMapping: mappers.multiSelect({
      valueMap: {
        kidney_disease_diabetes: [
          { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETIC_NEPHROPATHY, booleanAttributeValue: true },
        ],
        foot_ulcers_diabetes: [
          { attributeDefinitionId: 21301, booleanAttributeValue: true }, // has_diabetic_neuropathy_ulcers
        ],
        nerve_damage_diabetes: [
          { attributeDefinitionId: 20412, booleanAttributeValue: true }, // has_diabeticneuropathy
        ],
        eye_disease_diabetes: [
          { attributeDefinitionId: 20416, booleanAttributeValue: true }, // has_diabeticeyedisease
        ],
      },
      noneValue: "none_diabetes_complications",
      noneAttributeMap: {
        none_diabetes_complications: [
          { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETIC_NEPHROPATHY, booleanAttributeValue: false },
          { attributeDefinitionId: 21301, booleanAttributeValue: false }, // has_diabetic_neuropathy_ulcers
          { attributeDefinitionId: 20412, booleanAttributeValue: false }, // has_diabeticneuropathy
          { attributeDefinitionId: 20416, booleanAttributeValue: false }, // has_diabeticeyedisease
        ],
      },
    }),
  },

  // Q7: Health Conditions
  {
    id: "qualifying_conditions_have",
    type: QuestionType.CHECKBOX,
    question: "Do you have any of the following health conditions?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "cardiovascular_history",
        label: "History of heart attack, stroke, angina, or cardiovascular procedures (stents, bypass, angioplasty)",
      },
      {
        value: "osteoporosis",
        label: "Osteoporosis (brittle bones)",
      },
      {
        value: "hypertension_two_meds",
        label: "High blood pressure requiring 2 or more blood pressure medications",
      },
      {
        value: "cushings_with_surgery_status",
        label: "Cushing's syndrome that has not been successfully treated with surgery",
      },
      {
        value: "none_conditions",
        label: "None of these apply to me",
      },
    ],
    attributeMapping: mappers.multiSelect({
      valueMap: {
        cardiovascular_history: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: true },
        ],
        osteoporosis: [
          { attributeDefinitionId: 21016, booleanAttributeValue: true }, // has_osteoporosis
        ],
        hypertension_two_meds: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HYPERTENSION, booleanAttributeValue: true },
        ],
        cushings_with_surgery_status: [
          { attributeDefinitionId: 21521, booleanAttributeValue: true }, // has_cushings
        ],
      },
      noneValue: "none_conditions",
      noneAttributeMap: {
        none_conditions: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: false },
          { attributeDefinitionId: 21016, booleanAttributeValue: false }, // has_osteoporosis
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HYPERTENSION, booleanAttributeValue: false },
          { attributeDefinitionId: 21521, booleanAttributeValue: false }, // has_cushings
        ],
      },
    }),
  },

  // Q8: Qualifying Medications (auto-qualification checks checkbox array from Q5)
  {
    id: "qualifying_medications_take",
    type: QuestionType.CHECKBOX,
    question: "Are you currently taking any of these medications?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "insulin_daily",
        label: "Daily insulin injections",
      },
      {
        value: "glp1_medication",
        label: "GLP-1 injections (Ozempic, Mounjaro, Wegovy, Victoza, etc.)",
      },
      {
        value: "none_medications",
        label: "Neither of the above",
        disqualify: (allAnswers: Record<string, string | string[] | number | null | undefined>) => {
          // If they're taking 3+ medications (from checkbox Q5), they auto-qualify
          const medications = allAnswers?.["diabetes_medications_count"] as string[] | undefined;
          const medCount = Array.isArray(medications)
            ? medications.filter((s) => s !== "none_diabetes_meds").length
            : 0;
          if (medCount >= 3) {
            return false;
          }

          // Otherwise, disqualify ONLY if they selected "none" in ALL THREE qualifying questions
          const q6Answers = allAnswers?.["diabetes_complications"] as string[];
          const q7Answers = allAnswers?.["qualifying_conditions_have"] as string[];
          const q8Answers = allAnswers?.["qualifying_medications_take"] as string[];

          // Check if "none" was selected in Q6 (diabetes complications)
          const q6None = Array.isArray(q6Answers) && q6Answers.includes("none_diabetes_complications") && q6Answers.length === 1;

          // Check if "none" was selected in Q7 (health conditions)
          const q7None = Array.isArray(q7Answers) && q7Answers.includes("none_conditions") && q7Answers.length === 1;

          // Check if "none" was selected in Q8 (medications)
          const q8None = Array.isArray(q8Answers) && q8Answers.includes("none_medications") && q8Answers.length === 1;

          // Disqualify only if ALL three are "none"
          return q6None && q7None && q8None;
        },
      },
    ],
    disqualifyMessage:
      "This study requires participants to have at least one of the listed qualifying conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        insulin_daily: [
          { attributeDefinitionId: 23574, booleanAttributeValue: true }, // has_insulin_daily
        ],
      },
      noneValue: "none_medications",
      noneAttributeMap: {
        none_medications: [
          { attributeDefinitionId: 23574, booleanAttributeValue: false }, // has_insulin_daily
        ],
      },
    }),
  },

  // Q9: Blood Disorders (exclusion)
  {
    id: "blood_disorders",
    type: QuestionType.CHECKBOX,
    question: "Have you been diagnosed with any of the following blood conditions?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "hemolytic_anemia",
        label: "Hemolytic anemia",
        disqualify: true,
      },
      {
        value: "sickle_cell",
        label: "Sickle cell disease",
        disqualify: true,
      },
      {
        value: "itp",
        label: "Immune Thrombocytopenic Purpura (ITP)",
        disqualify: true,
      },
      {
        value: "none_blood_disorders",
        label: "None of the above",
      },
    ],
    disqualifyMessage:
      "For safety reasons, this study cannot include participants with these blood conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        hemolytic_anemia: [],
        sickle_cell: [],
        itp: [],
      },
      noneValue: "none_blood_disorders",
      noneAttributeMap: {
        none_blood_disorders: [],
      },
    }),
  },

  // Q10: Serious Medical Conditions (exclusion)
  {
    id: "serious_medical_conditions",
    type: QuestionType.CHECKBOX,
    question: "Have you been diagnosed with any of the following conditions?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "pheochromocytoma",
        label: "Known or suspected pheochromocytoma",
        disqualify: true,
      },
      {
        value: "eating_disorder",
        label: "Eating disorder as diagnosed by a doctor or psychologist",
        disqualify: true,
      },
      {
        value: "multiple_sclerosis",
        label: "Multiple sclerosis (MS)",
        disqualify: true,
      },
      {
        value: "liver_failure",
        label: "Liver failure or cirrhosis",
        disqualify: true,
      },
      {
        value: "eskd",
        label: "End-stage kidney disease (ESKD) or currently receiving dialysis",
        disqualify: true,
      },
      {
        value: "none_medical",
        label: "None of the above",
      },
    ],
    disqualifyMessage:
      "For safety reasons, this study cannot include participants with these medical conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        eating_disorder: [
          { attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_EATING_DISORDER, booleanAttributeValue: true },
        ],
        multiple_sclerosis: [
          { attributeDefinitionId: NEUROLOGICAL_ATTRIBUTES.HAS_MULTIPLE_SCLEROSIS, booleanAttributeValue: true },
        ],
        liver_failure: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: true },
        ],
        eskd: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: true },
        ],
      },
      noneValue: "none_medical",
      noneAttributeMap: {
        none_medical: [
          { attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_EATING_DISORDER, booleanAttributeValue: false },
          { attributeDefinitionId: NEUROLOGICAL_ATTRIBUTES.HAS_MULTIPLE_SCLEROSIS, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: false },
        ],
      },
    }),
  },

  // Q11: Cancer and Pregnancy (exclusion)
  {
    id: "cancer_pregnancy",
    type: QuestionType.CHECKBOX,
    question: "Do any of the following apply to you?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "current_cancer_treatment",
        label: "I am currently receiving treatment for cancer",
        disqualify: true,
      },
      {
        value: "pregnant_or_breastfeeding",
        label: "I am currently pregnant or breastfeeding",
        disqualify: true,
      },
      {
        value: "none_cancer_pregnancy",
        label: "None of the above",
      },
    ],
    disqualifyMessage:
      "For safety reasons, this study cannot include participants who are pregnant, breastfeeding, or currently receiving cancer treatment.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        current_cancer_treatment: [
          { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: true },
        ],
        pregnant_or_breastfeeding: [
          { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: true },
        ],
      },
      noneValue: "none_cancer_pregnancy",
      noneAttributeMap: {
        none_cancer_pregnancy: [
          { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: false },
          { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: false },
        ],
      },
    }),
  },

  // Q12: Clinical Trial Participation (exclusion)
  {
    id: "other_clinical_trials",
    type: QuestionType.RADIO,
    question:
      "Are you currently enrolling in or participating in any other clinical trials?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, I am not enrolling in or participating in any other clinical trials",
      },
      {
        value: "yes",
        label: "Yes, I am currently enrolling in or participating in another clinical trial",
        disqualify: true,
      },
    ],
    disqualifyMessage: "Participants can only participate in one clinical trial at a time.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
      yes: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true },
      ],
    }),
  },
];
